// src/pages/ELearning/TestConstructor.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getTestById,
  createTest,
  updateTest,
  getQuestions,
  createQuestion,
  updateQuestion,
  softDeleteTest,
  importQuestionsExcel,
  getQuestionsLibrary,
  importQuestionsFromLibrary,
} from '../../api/client';
import { Plus, Trash2, Save, X, Loader2, AlertCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';

interface Question {
  _id?: string;
  questionText: string;
  questionType: 'single' | 'multiple' | 'text';
  options: { text: string; isCorrect: boolean }[];
  correctAnswer?: string;
  points: number;
  order: number;
}

interface TestForm {
  title: string;
  description: string;
  category: string;
  timeLimit: number;
  points: number;
  passingScore: number;
  isActive: boolean;
  image?: string;
  tags?: string[];
}

type OptionField = keyof Question['options'][number];

const TestConstructor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagsInput, setTagsInput] = useState('');
  const [testForm, setTestForm] = useState<TestForm>({
    title: '',
    description: '',
    category: 'Общие',
    timeLimit: 20,
    points: 100,
    passingScore: 70,
    isActive: true,
    image: '',
    tags: [],
  });
  const [questions, setQuestions] = useState<Question[]>([]);

  // ─── ФИЧА Q: Импорт из Excel ───
  const [importExcelOpen, setImportExcelOpen] = useState(false);
  const [importExcelFile, setImportExcelFile] = useState<File | null>(null);
  const [importingExcel, setImportingExcel] = useState(false);

  // ─── ФИЧА E: Библиотека вопросов ───
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryItems, setLibraryItems] = useState<any[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySelected, setLibrarySelected] = useState<Set<string>>(new Set());
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryType, setLibraryType] = useState<string>('all');

  const loadLibrary = async () => {
    setLibraryLoading(true);
    try {
      const params: any = { limit: 100 };
      if (librarySearch) params.search = librarySearch;
      if (libraryType !== 'all') params.type = libraryType;
      if (id) params.excludeTestId = id;
      const data = await getQuestionsLibrary(params);
      setLibraryItems(data.items || []);
    } catch (e) {
      console.error('Library error:', e);
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    if (libraryOpen) loadLibrary();
    // eslint-disable-next-line
  }, [libraryOpen, librarySearch, libraryType]);

  const handleImportExcel = async () => {
    if (!id) {
      showToast('Сначала сохраните тест', 'info');
      return;
    }
    if (!importExcelFile) {
      showToast('Выберите файл', 'info');
      return;
    }
    setImportingExcel(true);
    try {
      const res = await importQuestionsExcel(id, importExcelFile);
      showToast('Импортировано: ' + (res.imported || 0) + ' вопросов' + (res.errors?.length ? ', ошибок: ' + res.errors.length : ''), 'success');
      setImportExcelOpen(false);
      setImportExcelFile(null);
      window.location.reload();
    } catch (e: any) {
      showToast('Ошибка: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setImportingExcel(false);
    }
  };

  const handleImportFromLibrary = async () => {
    if (!id) {
      showToast('Сначала сохраните тест', 'info');
      return;
    }
    if (librarySelected.size === 0) {
      showToast('Выберите хотя бы один вопрос', 'info');
      return;
    }
    try {
      const res = await importQuestionsFromLibrary(id, Array.from(librarySelected));
      showToast('Добавлено: ' + (res.imported || 0), 'success');
      setLibraryOpen(false);
      setLibrarySelected(new Set());
      window.location.reload();
    } catch (e: any) {
      showToast('Ошибка: ' + (e.response?.data?.error || e.message), 'error');
    }
  };
  const [imagePreview, setImagePreview] = useState<string>('');

  const categories = ['Мотивация', 'Лидерство', 'Комфорт', 'Лояльность', 'Общие'];

  useEffect(() => {
    if (id) {
      const fetchTest = async () => {
        setLoading(true);
        setError(null);
        try {
          const [testData, questionsData] = await Promise.all([
            getTestById(id),
            getQuestions(id),
          ]);
          setTestForm({
            title: testData.title,
            description: testData.description || '',
            category: testData.category,
            timeLimit: testData.timeLimit,
            points: testData.points,
            passingScore: testData.passingScore,
            isActive: testData.isActive,
            image: testData.image || '',
          });
          setImagePreview(testData.image || '');
          setQuestions(questionsData);
        } catch (err: any) {
          console.error('Ошибка загрузки теста:', err);
          setError(err.response?.data?.error || 'Не удалось загрузить тест');
        } finally {
          setLoading(false);
        }
      };
      fetchTest();
    }
  }, [id]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setTestForm({ ...testForm, image: base64 });
      setImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteTest = () => {
    if (!id) return;
    confirm.showConfirm({
      title: 'Удаление теста',
      message: 'Переместить тест в корзину?',
      confirmText: 'Удалить',
      onConfirm: async () => {
        try {
          await softDeleteTest(id);
          showToast('Тест перемещён в корзину', 'success');
          navigate('/app/elearning/tests');
        } catch (err) {
          showToast('Не удалось удалить тест', 'error');
        }
      },
    });
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        questionText: '',
        questionType: 'single',
        options: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
        ],
        points: 10,
        order: questions.length + 1,
      },
    ]);
  };

  const updateQuestionField = (index: number, field: keyof Question, value: any) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const addOption = (qIndex: number) => {
    const updated = [...questions];
    updated[qIndex].options.push({ text: '', isCorrect: false });
    setQuestions(updated);
  };

  const updateOptionField = <K extends OptionField>(
    qIndex: number,
    oIndex: number,
    field: K,
    value: Question['options'][number][K]
  ) => {
    const updated = [...questions];
    updated[qIndex].options[oIndex][field] = value;
    setQuestions(updated);
  };

  const removeOption = (qIndex: number, oIndex: number) => {
    const updated = [...questions];
    updated[qIndex].options.splice(oIndex, 1);
    setQuestions(updated);
  };

  const removeQuestion = (index: number) => {
    confirm.showConfirm({
      title: 'Удаление вопроса',
      message: 'Вы уверены, что хотите удалить этот вопрос?',
      confirmText: 'Удалить',
      onConfirm: () => {
        const updated = questions.filter((_, i) => i !== index);
        setQuestions(updated);
      },
    });
  };

  const handleSubmit = async () => {
    if (!testForm.title) {
      showToast('Введите название теста', 'error');
      return;
    }
    setSaving(true);
    try {
      let testId = id;
      if (id) {
        await updateTest(id, testForm);
      } else {
        const res = await createTest(testForm);
        testId = res._id;
      }

      for (const q of questions) {
        const payload = { ...q, testId };
        if (q._id) {
          await updateQuestion(q._id, payload);
        } else {
          await createQuestion(payload);
        }
      }

      showToast('Тест сохранён!', 'success');
      navigate('/app/elearning/tests');
    } catch (err: any) {
      console.error('Ошибка сохранения:', err);
      showToast(err.response?.data?.error || 'Не удалось сохранить тест', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ff4d4f" />
          <h2>{error}</h2>
          <button onClick={() => navigate('/app/elearning/tests')} style={styles.retryButton}>
            Вернуться к тестам
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        .tc-hero { animation: fadeUp 0.5s ease-out; }
        .tc-card { transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); }
        .tc-input:focus { border-color: transparent !important; box-shadow: 0 0 0 2px #667eea !important; }
        .tc-q-card { transition: all 0.3s ease; animation: fadeUp 0.4s ease-out backwards; }
        .tc-q-card:hover { box-shadow: 0 12px 40px rgba(31, 38, 135, 0.12) !important; }
        .tc-btn-primary { transition: all 0.3s ease; }
        .tc-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important; }
        .tc-add-btn { transition: all 0.25s ease; }
        .tc-add-btn:hover { background: rgba(102, 126, 234, 0.08) !important; }
        .tc-toggle { position: relative; width: 48px; height: 26px; border-radius: 13px; cursor: pointer; transition: all 0.3s; }
        .tc-toggle-knob { position: absolute; top: 3px; width: 20px; height: 20px; background: #fff; border-radius: 50%; box-shadow: 0 2px 6px rgba(0,0,0,0.2); transition: all 0.3s; }
        .tc-icon-btn { transition: all 0.2s; border-radius: 8px; }
        .tc-icon-btn:hover { background: rgba(0,0,0,0.05); }
      `}</style>

      <div style={styles.container}>
        {/* ═══ Hero ═══ */}
        <div className="tc-hero" style={styles.hero}>
          <div style={styles.heroIcon}>
            <Save size={28} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={styles.title}>
              {id ? 'Редактирование теста' : 'Новый тест'}
            </h1>
            <p style={styles.subtitle}>
              {id ? 'Обновите параметры и вопросы' : 'Создайте новый тест для сотрудников'}
            </p>
          </div>
          {id && (
            <button onClick={handleDeleteTest} style={styles.deleteButton}>
              <Trash2 size={16} /> Удалить
            </button>
          )}
        </div>

        {/* ═══ Секция 1: Основное ═══ */}
        <div className="tc-card" style={styles.section}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>1</span>
            <h2 style={styles.sectionTitle}>Основное</h2>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Название *</label>
            <input
              type="text"
              className="tc-input"
              value={testForm.title}
              onChange={e => setTestForm({ ...testForm, title: e.target.value })}
              style={styles.input}
              placeholder="Например: Кибербезопасность для всех"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Описание</label>
            <textarea
              className="tc-input"
              value={testForm.description}
              onChange={e => setTestForm({ ...testForm, description: e.target.value })}
              style={styles.textarea}
              placeholder="Краткое описание теста"
            />
          </div>

        {/* ─── ФИЧА D: Теги ─── */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Теги (через запятую)</label>
          <input
            type="text"
            className="tc-input"
            placeholder="безопасность, комплаенс, onboarding"
            value={tagsInput}
            onChange={e => {
              const val = e.target.value;
              setTagsInput(val);
              const tags = val.split(',').map(s => s.trim()).filter(Boolean);
              setTestForm({ ...testForm, tags });
            }}
            style={styles.input}
          />
          {tagsInput && (
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {tagsInput.split(',').map(s => s.trim()).filter(Boolean).map((tag, i) => (
                <span key={i} style={{
                  padding: '3px 10px',
                  background: 'rgba(102, 126, 234, 0.12)',
                  color: '#667eea',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                }}>
                  🏷️ {tag}
                </span>
              ))}
            </div>
          )}
        </div>

          <div style={styles.grid4}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Категория</label>
              <select
                className="tc-input"
                value={testForm.category}
                onChange={e => setTestForm({ ...testForm, category: e.target.value })}
                style={styles.input}
              >
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Время (мин)</label>
              <input
                type="number"
                className="tc-input"
                value={testForm.timeLimit}
                onChange={e => setTestForm({ ...testForm, timeLimit: +e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Баллы</label>
              <input
                type="number"
                className="tc-input"
                value={testForm.points}
                onChange={e => setTestForm({ ...testForm, points: +e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Проходной %</label>
              <input
                type="number"
                className="tc-input"
                value={testForm.passingScore}
                onChange={e => setTestForm({ ...testForm, passingScore: +e.target.value })}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.toggleRow}>
            <div
              className="tc-toggle"
              onClick={() => setTestForm({ ...testForm, isActive: !testForm.isActive })}
              style={{
                background: testForm.isActive
                  ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                  : 'rgba(0,0,0,0.15)',
              }}
            >
              <div
                className="tc-toggle-knob"
                style={{ left: testForm.isActive ? '25px' : '3px' }}
              />
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: testForm.isActive ? '#10b981' : '#8b8fa8' }}>
              {testForm.isActive ? 'Активен' : 'Черновик'}
            </span>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Изображение</label>
            <div style={styles.imageUpload}>
              {imagePreview ? (
                <img src={imagePreview} alt="preview" style={styles.preview} />
              ) : (
                <div style={styles.imagePlaceholder}>
                  <ImageIcon size={32} color="#8b8fa8" />
                </div>
              )}
              <label style={styles.uploadLabel}>
                <Upload size={16} /> Загрузить
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
              </label>
            </div>
          </div>
        </div>

        {/* ═══ Секция 2: Вопросы ═══ */}
        <div className="tc-card" style={styles.section}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>2</span>
            <h2 style={styles.sectionTitle}>Вопросы</h2>
            <span style={styles.counter}>{questions.length}</span>
          </div>

          {/* ─── ФИЧА Q + E: Импорт ─── */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button
              onClick={() => setImportExcelOpen(true)}
              style={{
                padding: '10px 16px',
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#fff', border: 'none', borderRadius: 12,
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                boxShadow: '0 6px 16px rgba(17, 153, 142, 0.3)',
              }}
              title="Импортировать вопросы из XLSX"
            >
              📥 Импорт Excel
            </button>
            <button
              onClick={() => setLibraryOpen(true)}
              style={{
                padding: '10px 16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff', border: 'none', borderRadius: 12,
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)',
              }}
              title="Добавить вопросы из библиотеки"
            >
              📚 Из библиотеки
            </button>
          </div>

          {questions.map((q, qIndex) => (
            <div
              key={qIndex}
              className="tc-q-card"
              style={{ ...styles.questionCard, animationDelay: `${qIndex * 0.05}s` }}
            >
              <div style={styles.questionHeader}>
                <span style={styles.qNumber}>Вопрос {qIndex + 1}</span>
                <button onClick={() => removeQuestion(qIndex)} className="tc-icon-btn" style={styles.iconButton}>
                  <Trash2 size={16} color="#ef4444" />
                </button>
              </div>

              <input
                type="text"
                className="tc-input"
                placeholder="Текст вопроса"
                value={q.questionText}
                onChange={e => updateQuestionField(qIndex, 'questionText', e.target.value)}
                style={styles.input}
              />

              <div style={styles.row}>
                <select
                  className="tc-input"
                  value={q.questionType}
                  onChange={e => updateQuestionField(qIndex, 'questionType', e.target.value)}
                  style={{ ...styles.input, flex: 1 }}
                >
                  <option value="single">Один ответ</option>
                  <option value="multiple">Несколько ответов</option>
                  <option value="text">Текстовый ответ</option>
                </select>
                <input
                  type="number"
                  className="tc-input"
                  placeholder="Баллы"
                  value={q.points}
                  onChange={e => updateQuestionField(qIndex, 'points', +e.target.value)}
                  style={{ ...styles.input, width: 100 }}
                />
              </div>

              {q.questionType !== 'text' ? (
                <div>
                  {q.options.map((opt, oIndex) => (
                    <div key={oIndex} style={styles.optionRow}>
                      <input
                        type="text"
                        className="tc-input"
                        placeholder={`Вариант ${oIndex + 1}`}
                        value={opt.text}
                        onChange={e => updateOptionField(qIndex, oIndex, 'text', e.target.value)}
                        style={{ ...styles.input, flex: 1 }}
                      />
                      <label style={styles.checkLabel}>
                        <input
                          type={q.questionType === 'single' ? 'radio' : 'checkbox'}
                          name={`correct-${qIndex}`}
                          checked={opt.isCorrect}
                          onChange={e => updateOptionField(qIndex, oIndex, 'isCorrect', e.target.checked)}
                          style={{ accentColor: '#667eea', width: 16, height: 16 }}
                        />
                        <span>Правильный</span>
                      </label>
                      <button onClick={() => removeOption(qIndex, oIndex)} className="tc-icon-btn" style={styles.iconButton}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => addOption(qIndex)} className="tc-add-btn" style={styles.addOptionButton}>
                    <Plus size={14} /> Добавить вариант
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  className="tc-input"
                  placeholder="Правильный ответ"
                  value={q.correctAnswer || ''}
                  onChange={e => updateQuestionField(qIndex, 'correctAnswer', e.target.value)}
                  style={styles.input}
                />
              )}
            </div>
          ))}

          <button onClick={addQuestion} className="tc-add-btn" style={styles.addQuestionButton}>
            <Plus size={16} /> Добавить вопрос
          </button>
        </div>

        {/* ═══ Actions ═══ */}
        <div style={styles.actions}>
          <button onClick={handleSubmit} disabled={saving} className="tc-btn-primary" style={styles.saveButton}>
            {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button onClick={() => navigate('/app/elearning/tests')} style={styles.cancelButton}>
            Отмена
          </button>
        </div>
      </div>

      {/* ═══ ФИЧА Q: Модал импорта из Excel ═══ */}
      {importExcelOpen && (
        <div style={modalOverlay} onClick={() => setImportExcelOpen(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>
              📥 Импорт вопросов из Excel
            </h3>
            <p style={{ color: '#8b8fa8', fontSize: 13, marginBottom: 16 }}>
              XLSX с колонками: Вопрос, Тип, Вариант 1-5, Правильный ответ, Баллы
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={e => setImportExcelFile(e.target.files?.[0] || null)}
              style={{ marginBottom: 16, fontSize: 14 }}
            />
            {importExcelFile && (
              <p style={{ fontSize: 12, color: '#5a5f7a', marginBottom: 12 }}>
                📎 {importExcelFile.name} ({(importExcelFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setImportExcelOpen(false)}
                style={{ padding: '10px 18px', background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                Отмена
              </button>
              <button
                onClick={handleImportExcel}
                disabled={importingExcel || !importExcelFile}
                style={{
                  padding: '10px 18px',
                  background: importingExcel ? '#95a5a6' : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700,
                  cursor: importingExcel ? 'wait' : 'pointer',
                }}
              >
                {importingExcel ? 'Импорт...' : 'Импортировать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ФИЧА E: Модал библиотеки вопросов ═══ */}
      {libraryOpen && (
        <div style={modalOverlay} onClick={() => setLibraryOpen(false)}>
          <div style={{ ...modalBox, maxWidth: 800, maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>
              📚 Библиотека вопросов
            </h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Поиск..."
                value={librarySearch}
                onChange={e => setLibrarySearch(e.target.value)}
                style={{ flex: 1, padding: '10px 14px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', fontSize: 14 }}
              />
              <select
                value={libraryType}
                onChange={e => setLibraryType(e.target.value)}
                style={{ padding: '10px 14px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', fontSize: 14 }}
              >
                <option value="all">Все типы</option>
                <option value="single">Один ответ</option>
                <option value="multiple">Несколько</option>
                <option value="text">Текст</option>
              </select>
            </div>

            {libraryLoading ? (
              <p style={{ textAlign: 'center', padding: 40 }}>Загрузка...</p>
            ) : libraryItems.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999', padding: 40 }}>Ничего не найдено</p>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 12 }}>
                {libraryItems.map(q => (
                  <label
                    key={q._id}
                    style={{
                      display: 'flex', gap: 12, padding: '12px 14px',
                      borderBottom: '1px solid rgba(0,0,0,0.05)',
                      cursor: 'pointer',
                      background: librarySelected.has(q._id) ? 'rgba(102,126,234,0.08)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={librarySelected.has(q._id)}
                      onChange={() => {
                        const next = new Set(librarySelected);
                        if (next.has(q._id)) next.delete(q._id); else next.add(q._id);
                        setLibrarySelected(next);
                      }}
                      style={{ marginTop: 4 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e', marginBottom: 4 }}>
                        {q.questionText}
                      </div>
                      <div style={{ fontSize: 11, color: '#8b8fa8' }}>
                        📚 {q.testTitle} • {q.questionType === 'single' ? 'Один ответ' : q.questionType === 'multiple' ? 'Несколько' : 'Текст'} • {q.points} балл.
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
              <span style={{ fontSize: 13, color: '#8b8fa8', alignSelf: 'center' }}>
                Выбрано: {librarySelected.size}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setLibraryOpen(false)}
                  style={{ padding: '10px 18px', background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  Отмена
                </button>
                <button
                  onClick={handleImportFromLibrary}
                  disabled={librarySelected.size === 0}
                  style={{
                    padding: '10px 18px',
                    background: librarySelected.size === 0 ? '#95a5a6' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700,
                    cursor: librarySelected.size === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  Добавить ({librarySelected.size})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const modalOverlay: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(8px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 2000, padding: 20,
};
const modalBox: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(24px)',
  borderRadius: 24, padding: 28,
  maxWidth: 520, width: '100%',
  boxShadow: '0 24px 64px rgba(31, 38, 135, 0.25)',
  border: '1px solid rgba(255, 255, 255, 0.7)',
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 1000, margin: '0 auto' },
  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 },
  heroIcon: { width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #667eea 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },
  deleteButton: { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white', border: 'none', borderRadius: 14, padding: '10px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14, boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)' },
  section: { background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', border: '1px solid rgba(255, 255, 255, 0.7)', borderRadius: 24, padding: 24, marginBottom: 20, boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  sectionBadge: { width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)' },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' },
  counter: { marginLeft: 'auto', padding: '4px 12px', background: 'rgba(102, 126, 234, 0.12)', color: '#667eea', borderRadius: 12, fontSize: 13, fontWeight: 700 },
  formGroup: { marginBottom: 16 },
  label: { display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#5a5f7a' },
  input: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)', fontSize: 14, outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box', fontFamily: 'inherit' },
  textarea: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)', fontSize: 14, minHeight: 80, outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' },
  select: { padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)', fontSize: 14, outline: 'none', fontFamily: 'inherit' },
  row: { display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 },
  toggleRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '12px 0' },
  imageUpload: { display: 'flex', alignItems: 'center', gap: 16 },
  preview: { width: 100, height: 100, objectFit: 'cover', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
  imagePlaceholder: { width: 100, height: 100, background: 'rgba(0,0,0,0.04)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed rgba(102,126,234,0.3)' },
  uploadLabel: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'linear-gradient(135deg, #2575fc 0%, #6a11cb 100%)', color: 'white', borderRadius: 12, cursor: 'pointer', fontWeight: 600, fontSize: 14, boxShadow: '0 8px 20px rgba(37,117,252,0.3)' },
  questionCard: { background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(12px)', borderRadius: 18, padding: 20, marginBottom: 16, border: '1px solid rgba(255, 255, 255, 0.6)', boxShadow: '0 4px 16px rgba(31, 38, 135, 0.05)' },
  questionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  qNumber: { fontWeight: 700, fontSize: 14, color: '#667eea', padding: '4px 12px', background: 'rgba(102, 126, 234, 0.1)', borderRadius: 10 },
  optionRow: { display: 'flex', alignItems: 'center', marginTop: 8, gap: 8 },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#5a5f7a', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' },
  iconButton: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  addOptionButton: { background: 'transparent', border: '1.5px dashed rgba(102,126,234,0.4)', borderRadius: 12, padding: '8px 14px', color: '#667eea', cursor: 'pointer', marginTop: 10, fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 },
  addQuestionButton: { background: 'transparent', border: '2px dashed rgba(102,126,234,0.3)', borderRadius: 16, padding: '16px 20px', color: '#667eea', cursor: 'pointer', width: '100%', marginTop: 8, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  actions: { display: 'flex', gap: 12, marginTop: 20, marginBottom: 40 },
  saveButton: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: 14, padding: '14px 28px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)' },
  cancelButton: { background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '14px 28px', cursor: 'pointer', fontWeight: 600, fontSize: 15, color: '#5a5f7a' },
  retryButton: { marginTop: 20, padding: '10px 24px', background: '#2575fc', color: 'white', border: 'none', borderRadius: 30, cursor: 'pointer', fontWeight: 600 }
};

export default TestConstructor;